import { useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Check, ChevronDown, Users } from 'lucide-react';

/**
 * A pill-styled, type-to-filter single-select combobox.
 * Starts on a "none" option (e.g. "No Workspace" / "No Team"); the search box
 * appears automatically once there are enough options to warrant filtering.
 */
export function FilterSelect({
  value,
  options = [],
  placeholder = 'None',
  icon: Icon = Users,
  onChange,
  testid,
  searchThreshold = 5,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  const showSearch = options.length >= searchThreshold;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="fs-chip" data-testid={testid} disabled={disabled}>
          {Icon && <Icon size={12} className="fs-chip-ic" />}
          <span className="fs-chip-label">{selected ? selected.label : placeholder}</span>
          <ChevronDown size={12} className="fs-chip-caret" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-64 bg-white border-border" data-testid={testid ? `${testid}-menu` : undefined}>
        <Command className="bg-white">
          {showSearch && <CommandInput placeholder="Search…" className="h-9 text-sm" />}
          <CommandList>
            <CommandEmpty>No matches.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={placeholder}
                onSelect={() => { onChange(''); setOpen(false); }}
                data-testid={testid ? `${testid}-none` : undefined}
                className="text-sm"
              >
                <span className="flex-1 truncate text-muted-foreground">{placeholder}</span>
                {!value && <Check size={14} className="text-[#6994c6]" />}
              </CommandItem>
              {options.map(o => (
                <CommandItem
                  key={o.value}
                  value={o.label}
                  onSelect={() => { onChange(o.value); setOpen(false); }}
                  data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                  className="text-sm"
                >
                  <span className="flex-1 truncate">{o.label}</span>
                  {value === o.value && <Check size={14} className="text-[#6994c6]" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
